import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { collectClientAnonymizeHints } from './anonymizeForAi';

const mergeAiRequestBody = (body) => {
  const base = body && typeof body === 'object' ? { ...body } : {};
  const fromCaller = base.anonymizeHints?.names;
  const names = collectClientAnonymizeHints(Array.isArray(fromCaller) ? fromCaller : []);
  const explicitUserName = String(base.user_name || '').trim().toLowerCase();
  const filteredNames = explicitUserName
    ? names.filter((n) => String(n || '').trim().toLowerCase() !== explicitUserName)
    : names;
  return { ...base, anonymizeHints: { names: filteredNames } };
};

/** Production Cloud Run API (same project). Override with REACT_APP_CLOUD_RUN_API_BASE_URL for staging. */
const DEFAULT_CLOUD_RUN_BASE = 'https://rebuildme-pro-api-824438072180.us-central1.run.app';

/** Visible SSE/stream text pacing in the UI (code points per second). */
const STREAM_DISPLAY_CHARS_PER_SECOND = 40;

const isNativeShell = () => {
  if (typeof window === 'undefined') return false;
  const proto = String(window.location?.protocol || '');
  if (proto === 'capacitor:' || proto === 'ionic:') return true;
  // iosScheme: "https" → document is https://localhost; still a native shell.
  const host = String(window.location?.hostname || '');
  if (proto === 'https:' && /^(localhost|127\.0\.0\.1)$/i.test(host)) {
    try {
      if (Capacitor?.isNativePlatform?.()) return true;
    } catch {
      /* ignore */
    }
  }
  try {
    return Boolean(Capacitor?.isNativePlatform?.());
  } catch {
    return false;
  }
};

/** Web / dev only: baked REACT_APP_CLOUD_RUN_API_BASE_URL (native ignores this — see getBaseUrl). */
const remoteBaseFromEnv = () => {
  const raw = String(process.env.REACT_APP_CLOUD_RUN_API_BASE_URL || '').trim().replace(/\/$/, '');
  if (!raw || !/^https?:\/\//i.test(raw)) return '';
  if (/localhost|127\.0\.0\.1/i.test(raw)) return '';
  return raw;
};

const getBaseUrl = () => {
  // Capacitor iOS/Android: never use Webpack env here — builds often embed an old *.a.run.app host that no
  // longer matches the live service (WKWebView → "Load failed"). Always use the shipped API base.
  if (isNativeShell()) {
    return DEFAULT_CLOUD_RUN_BASE;
  }

  const fromEnv = remoteBaseFromEnv();
  if (fromEnv) return fromEnv;

  if (process.env.NODE_ENV === 'production') {
    return DEFAULT_CLOUD_RUN_BASE;
  }

  return '';
};

const buildUrl = (path) => {
  const normalizedPath = String(path || '').startsWith('/') ? path : `/${path}`;
  let baseUrl = getBaseUrl();
  // Never call Cloud AI as a relative URL from the app bundle (WKWebView → "Load failed").
  if (/^\/api\//.test(normalizedPath) && (!baseUrl || !/^https?:\/\//i.test(baseUrl))) {
    baseUrl = DEFAULT_CLOUD_RUN_BASE;
  }
  return baseUrl ? `${baseUrl}${normalizedPath}` : normalizedPath;
};

const getAuthToken = async () => {
  const { getCurrentFirebaseIdToken } = await import('./firebase');
  return getCurrentFirebaseIdToken();
};

/** Native shell: WKWebView fetch() triggers CORS preflight; Cloud Run IAM returns 403 on OPTIONS before the app runs. Native HTTP skips that. */
const nativePostJson = async (url, body, token) => {
  const res = await CapacitorHttp.request({
    url,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    data: body ?? {},
  });
  let data = res.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      data = {};
    }
  }
  if (!data || typeof data !== 'object') data = {};
  return { status: res.status, data };
};

/**
 * Native shell: WKWebView `fetch` for AI *-once JSON posts. `CapacitorHttp` can fail when the
 * Firebase Bearer plus JSON body is large (native bridge limits); same pattern as photo upload / account DELETE.
 */
const nativeFetchJsonPost = async (url, body, token) => {
  let response;
  try {
    response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body ?? {}),
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e || 'Network error');
    throw new Error(
      msg.includes('Load failed') || msg === 'Failed to fetch'
        ? 'Could not reach the cloud API. Check your connection and try again.'
        : msg,
    );
  }
  const data = await response.json().catch(() => ({}));
  const safe = data && typeof data === 'object' ? data : {};
  return { status: response.status, data: safe };
};

const nativePutJson = async (url, body, token) => {
  const res = await CapacitorHttp.request({
    url,
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    data: body ?? {},
  });
  let data = res.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      data = {};
    }
  }
  if (!data || typeof data !== 'object') data = {};
  return { status: res.status, data };
};

/** Authenticated PUT JSON (native uses CapacitorHttp to avoid CORS preflight issues). */
export const putCloudJson = async (path, body) => {
  const url = buildUrl(path);
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Sign in with Google to use cloud features.');
  }
  const payload = body && typeof body === 'object' ? body : {};

  if (isNativeShell()) {
    const { status, data } = await nativePutJson(url, payload, token);
    if (status < 200 || status >= 300) {
      const message = data?.error || data?.message || `Request failed with status ${status}`;
      throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
    }
    return data;
  }

  const response = await fetch(url, {
    method: 'PUT',
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = data?.error || data?.message || `Request failed with status ${response.status}`;
    throw new Error(message);
  }

  return data;
};

/**
 * Upload a compressed journal photo as base64 JSON (avoids GCS v4 signed PUT, which needs iam.serviceAccounts.signBlob).
 * Uses fetch on native shells so large bodies are not forced through the Capacitor JSON bridge.
 */
export const journalPhotoUploadDirect = async ({ entryId, imageBase64 }) => {
  const url = buildUrl('/api/journal/photos/upload');
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Sign in with Google to use cloud features.');
  }
  const payload = { entryId: String(entryId || '').trim(), imageBase64: String(imageBase64 || '') };

  if (isNativeShell()) {
    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e || 'Network error');
      throw new Error(
        msg.includes('Load failed') || msg === 'Failed to fetch'
          ? 'Could not reach the cloud API. Check your connection and try again.'
          : msg,
      );
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = data?.error || data?.message || `Request failed with status ${response.status}`;
      throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
    }
    return data;
  }

  return postCloudAi('/api/journal/photos/upload', payload);
};

/** Authenticated fetch of a JPEG stored at `storagePath` (for UI when signed read URL is unavailable). */
export const fetchJournalPhotoObjectUrl = async (storagePath) => {
  const path = `/api/journal/photos/file?storagePath=${encodeURIComponent(String(storagePath || '').trim())}`;
  const url = buildUrl(path);
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Sign in with Google to use cloud features.');
  }
  const response = await fetch(url, {
    method: 'GET',
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(`Photo load failed (${response.status})`);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

export const postCloudAi = async (path, body) => {
  const url = buildUrl(path);
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Sign in with Google to use cloud features.');
  }

  const payload = String(path || '').startsWith('/api/ai') ? mergeAiRequestBody(body) : body;

  if (isNativeShell()) {
    const { status, data } = await nativePostJson(url, payload, token);
    if (status < 200 || status >= 300) {
      const message = data?.error || data?.message || `Request failed with status ${status}`;
      throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
    }
    return data;
  }

  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload || {}),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = data?.error || data?.message || `Request failed with status ${response.status}`;
    throw new Error(message);
  }

  return data;
};

/** PUT a JPEG blob to a GCS v4 signed upload URL (from `/api/journal/photos/init-upload`). */
export const putBlobToSignedUrl = async (uploadUrl, blob, contentType = 'image/jpeg') => {
  const response = await fetch(String(uploadUrl), {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: blob,
    mode: 'cors',
    cache: 'no-store',
  });
  if (!response.ok) {
    const t = await response.text().catch(() => '');
    throw new Error(`Photo upload failed (${response.status}) ${String(t).slice(0, 160)}`);
  }
};

export const journalPhotoInitUpload = async () => postCloudAi('/api/journal/photos/init-upload', {});

export const journalPhotoComplete = async ({ entryId, storagePath, photoId }) => postCloudAi(
  '/api/journal/photos/complete',
  { entryId, storagePath, photoId },
);

export const deleteCloudJournalEntry = async (entryId) => {
  const id = encodeURIComponent(String(entryId || '').trim());
  if (!id) {
    throw new Error('Missing entry id');
  }
  const path = `/api/journal/entries/${id}`;
  const url = buildUrl(path);
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Sign in with Google to use cloud features.');
  }

  if (isNativeShell()) {
    const res = await CapacitorHttp.request({
      url,
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    let data = res.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        data = {};
      }
    }
    if (res.status < 200 || res.status >= 300) {
      const message = data?.error || data?.message || `Request failed with status ${res.status}`;
      throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
    }
    return data;
  }

  const response = await fetch(url, {
    method: 'DELETE',
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = data?.error || data?.message || `Request failed with status ${response.status}`;
    throw new Error(message);
  }

  return data;
};

export const deleteCloudJournalPhoto = async (entryId, photoId) => {
  const eid = encodeURIComponent(String(entryId || '').trim());
  const pid = encodeURIComponent(String(photoId || '').trim());
  if (!eid || !pid) {
    throw new Error('Missing entry or photo id');
  }
  const path = `/api/journal/entries/${eid}/photos/${pid}`;
  const url = buildUrl(path);
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Sign in with Google to use cloud features.');
  }

  if (isNativeShell()) {
    const res = await CapacitorHttp.request({
      url,
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    let data = res.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        data = {};
      }
    }
    if (res.status < 200 || res.status >= 300) {
      const message = data?.error || data?.message || `Request failed with status ${res.status}`;
      throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
    }
    return data;
  }

  const response = await fetch(url, {
    method: 'DELETE',
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = data?.error || data?.message || `Request failed with status ${response.status}`;
    throw new Error(message);
  }

  return data;
};

export const getCloudJournalEntries = async (limit = 1) => {
  const safeLimit = Math.max(1, Math.min(Number(limit) || 1, 100));
  const path = `/api/journal/entries?limit=${safeLimit}`;
  const url = buildUrl(path);
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Sign in with Google to use cloud features.');
  }

  if (isNativeShell()) {
    const res = await CapacitorHttp.request({
      url,
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    let data = res.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        data = {};
      }
    }
    if (res.status < 200 || res.status >= 300) {
      const message = data?.error || data?.message || `Request failed with status ${res.status}`;
      throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
    }
    return Array.isArray(data?.entries) ? data.entries : [];
  }

  const response = await fetch(url, {
    method: 'GET',
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = data?.error || data?.message || `Request failed with status ${response.status}`;
    throw new Error(message);
  }
  return Array.isArray(data?.entries) ? data.entries : [];
};

export const deleteCloudAccountData = async () => {
  const url = buildUrl('/api/account');
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Sign in with Google to use cloud features.');
  }

  if (isNativeShell()) {
    // WKWebView fetch avoids Capacitor → native HTTP bridge limits (“argument too big”) on very large Bearer tokens.
    let response;
    try {
      response = await fetch(url, {
        method: 'DELETE',
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (e) {
      const msg = String(e?.message || e || 'Network error');
      throw new Error(
        msg.includes('Load failed') || msg === 'Failed to fetch'
          ? 'Could not reach the cloud API (network or access blocked). If this started after a deploy, ensure Cloud Run allows unauthenticated invoke for this service while the app still sends a Firebase token.'
          : msg,
      );
    }
    const raw = await response.text();
    let data = {};
    if (raw) {
      try {
        data = JSON.parse(raw);
      } catch {
        data = {};
      }
    }
    if (!response.ok) {
      const message = data?.error || data?.message || `Request failed with status ${response.status}`;
      throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
    }
    return data;
  }

  const response = await fetch(url, {
    method: 'DELETE',
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = data?.error || data?.message || `Request failed with status ${response.status}`;
    throw new Error(message);
  }

  return data;
};

export const getCloudUserPreferences = async () => {
  const url = buildUrl('/api/user/preferences');
  const token = await getAuthToken();
  if (!token) throw new Error('Sign in to use cloud features.');

  if (isNativeShell()) {
    const res = await CapacitorHttp.request({
      url,
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    const data = typeof res.data === 'string' ? JSON.parse(res.data || '{}') : (res.data || {});
    if (res.status < 200 || res.status >= 300) throw new Error(data?.error || data?.message || `Request failed with status ${res.status}`);
    return data?.preferences && typeof data.preferences === 'object' ? data.preferences : {};
  }

  const response = await fetch(url, {
    method: 'GET',
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data?.error || data?.message || `Request failed with status ${response.status}`);
  return data?.preferences && typeof data.preferences === 'object' ? data.preferences : {};
};

export const saveCloudUserPreferences = async (preferences) => {
  // Server exposes PUT /api/user/preferences (POST was never registered → 404).
  return putCloudJson('/api/user/preferences', { preferences });
};

/**
 * POST to a streaming AI endpoint (Server-Sent Events: `data: {...}\\n\\n`).
 * Invokes onDelta for each text delta, onDone once with { done, usageEstimate, text }, onError on failure.
 */
const streamFallbackPath = (path) => {
  if (path === '/api/ai/prompt-stream') return '/api/ai/prompt-once';
  if (path === '/api/ai/stream') return '/api/ai/stream-once';
  if (path === '/api/ai/coach-stream') return '/api/ai/coach-once';
  if (path === '/api/ai/onboarding-stream') return '/api/ai/onboarding-once';
  if (path === '/api/ai/import-notes-stream') return '/api/ai/import-notes-once';
  if (path === '/api/ai/welcome-stream') return '/api/ai/prompt-once';
  return null;
};

/** ~UTF-32 code point (one visible tick; pairs surrogate astral chars). */
const shiftFirstCodePoint = (s) => {
  if (!s) return ['', ''];
  const cp = s.codePointAt(0);
  if (cp === undefined) return ['', ''];
  const ch = String.fromCodePoint(cp);
  return [ch, s.slice(ch.length)];
};

/**
 * Smooths network/SSE bursts so the UI visibly streams (~`charsPerSecond` code points/sec).
 * Callers still receive incremental `onDelta` pieces and a deferred `onDone` after the queue drains.
 */
const createStreamDisplayThrottle = ({ onDelta, onDone, signal, charsPerSecond = STREAM_DISPLAY_CHARS_PER_SECOND }) => {
  let queue = '';
  let pumpRunning = false;
  let pendingDonePayload = null;
  const intervalMs = Math.max(20, Math.floor(1000 / Math.max(1, charsPerSecond)));

  const sleepStep = () =>
    new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      const id = setTimeout(resolve, intervalMs);
      const onAbort = () => {
        clearTimeout(id);
        reject(new DOMException('Aborted', 'AbortError'));
      };
      if (signal) signal.addEventListener('abort', onAbort, { once: true });
    });

  const tryFinish = () => {
    if (pendingDonePayload == null || queue.length > 0) return;
    const payload = pendingDonePayload;
    pendingDonePayload = null;
    try {
      onDone?.(payload);
    } catch {
      /* caller must not block stream completion */
    }
  };

  const pump = async () => {
    if (pumpRunning) return;
    pumpRunning = true;
    try {
      while (queue.length > 0) {
        const [ch, rest] = shiftFirstCodePoint(queue);
        queue = rest;
        if (ch) onDelta?.(ch);
        if (queue.length > 0) await sleepStep();
      }
      tryFinish();
    } catch {
      queue = '';
      pendingDonePayload = null;
    } finally {
      pumpRunning = false;
    }
  };

  const join = () =>
    new Promise((resolve, reject) => {
      const step = () => {
        if (signal?.aborted) {
          reject(new DOMException('Aborted', 'AbortError'));
          return;
        }
        const idle = !queue.length && !pumpRunning && pendingDonePayload === null;
        if (idle) {
          resolve();
          return;
        }
        setTimeout(step, Math.min(intervalMs, 24));
      };
      step();
    });

  return {
    delta(chunk) {
      const s = String(chunk ?? '');
      if (!s) return;
      queue += s;
      void pump();
    },
    /** Call when the server stream is finished; `onDone` runs after any queued text is shown. */
    complete(donePayload) {
      pendingDonePayload = donePayload;
      tryFinish();
    },
    join,
  };
};

/** Wall-clock cap so hung proxies / sockets cannot leave the UI on “Thinking…” forever. */
const DEFAULT_STREAM_TIMEOUT_MS = 120_000;

export const postCloudAiStream = async (
  path,
  body,
  {
    onDelta,
    onDone,
    onError,
    signal: externalSignal,
    timeoutMs = DEFAULT_STREAM_TIMEOUT_MS,
    charsPerSecond = STREAM_DISPLAY_CHARS_PER_SECOND,
  } = {},
) => {
  const url = buildUrl(path);
  const token = await getAuthToken();
  if (!token) {
    const err = new Error('Sign in with Google to use cloud features.');
    onError?.(err);
    throw err;
  }

  const streamController = new AbortController();
  const combinedSignal = streamController.signal;
  let timeoutId = null;
  const onExternalAbort = () => {
    streamController.abort();
  };
  if (externalSignal) {
    if (externalSignal.aborted) streamController.abort();
    else externalSignal.addEventListener('abort', onExternalAbort, { once: true });
  }
  if (typeof timeoutMs === 'number' && timeoutMs > 0) {
    timeoutId = setTimeout(() => streamController.abort(), timeoutMs);
  }
  const cleanupStreamGuards = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort);
  };

  const mergedBody = String(path || '').startsWith('/api/ai') ? mergeAiRequestBody(body) : body;

  const useThrottle = typeof onDelta === 'function';
  const throttle = useThrottle ? createStreamDisplayThrottle({ onDelta, onDone, signal: combinedSignal, charsPerSecond }) : null;
  const emitDelta = throttle ? (c) => throttle.delta(c) : onDelta;
  const emitDone = throttle ? (p) => throttle.complete(p) : onDone;
  const awaitDisplay = () => throttle?.join?.() ?? Promise.resolve();

  try {
  if (isNativeShell()) {
    const oncePath = streamFallbackPath(path);
    if (!oncePath) {
      const err = new Error(`Unsupported stream path for native: ${path}`);
      onError?.(err);
      throw err;
    }
    const nativeUrl = buildUrl(oncePath);
    if (typeof window !== 'undefined' && !window.__REBUILDME_CLOUD_API_LOG) {
      window.__REBUILDME_CLOUD_API_LOG = true;
      console.log('[cloudApi] AI request (native HTTP) →', nativeUrl.slice(0, 120));
    }
    let nativeResult;
    try {
      nativeResult = await nativeFetchJsonPost(nativeUrl, mergedBody, token);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      onError?.(err);
      throw err;
    }
    const { status, data } = nativeResult;
    if (status < 200 || status >= 300) {
      const message = data?.error || data?.message || `Request failed with status ${status}`;
      const err = new Error(typeof message === 'string' ? message : JSON.stringify(message));
      onError?.(err);
      throw err;
    }
    if (data?.error) {
      const err = new Error(String(data.error));
      onError?.(err);
      throw err;
    }
    const text = String(data.text ?? data.response ?? '');
    emitDelta?.(text);
    emitDone?.({ done: true, usageEstimate: Number(data.usageEstimate) || 0, text });
    await awaitDisplay();
    return;
  }

  if (typeof window !== 'undefined' && !window.__REBUILDME_CLOUD_API_LOG) {
    window.__REBUILDME_CLOUD_API_LOG = true;
    console.log('[cloudApi] AI request base →', getBaseUrl() || '(relative—unexpected)', 'full URL →', url.slice(0, 96));
  }

  const fetchInit = {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream, application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(mergedBody || {}),
    signal: combinedSignal,
  };

  let response;
  try {
    response = await fetch(url, fetchInit);
  } catch (networkErr) {
    const fb = streamFallbackPath(path);
    if (fb) {
      const msg = networkErr instanceof Error ? networkErr.message : String(networkErr);
      console.warn('[cloudApi] stream fetch failed, retrying non-streaming:', msg);
      try {
        response = await fetch(buildUrl(fb), fetchInit);
      } catch (e2) {
        const err = e2 instanceof Error ? e2 : new Error(String(e2));
        onError?.(err);
        throw err;
      }
    } else {
      const err = networkErr instanceof Error ? networkErr : new Error(String(networkErr));
      onError?.(err);
      throw err;
    }
  }

  const contentType = response.headers.get('content-type') || '';

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const message = data?.error || data?.message || `Request failed with status ${response.status}`;
    const err = new Error(typeof message === 'string' ? message : JSON.stringify(message));
    onError?.(err);
    throw err;
  }

  if (contentType.includes('application/json')) {
    const data = await response.json().catch(() => ({}));
    if (data?.error) {
      const err = new Error(String(data.error));
      onError?.(err);
      throw err;
    }
    const text = String(data.text ?? data.response ?? '');
    emitDelta?.(text);
    emitDone?.({ done: true, usageEstimate: Number(data.usageEstimate) || 0, text });
    await awaitDisplay();
    return;
  }

  if (!contentType.includes('text/event-stream') || !response.body) {
    const err = new Error(
      `AI streaming is temporarily unavailable (unexpected response format). Please try again shortly.`,
    );
    onError?.(err);
    throw err;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let carry = '';
  let sawDone = false;

  const handlePayload = (payload) => {
    if (payload.error != null && payload.error !== '') {
      const raw = payload.error;
      const text = typeof raw === 'string' ? raw : JSON.stringify(raw);
      onError?.(new Error(text));
      return true;
    }
    if (payload.done) {
      sawDone = true;
      emitDone?.(payload);
      return true;
    }
    if (payload.d) emitDelta?.(String(payload.d));
    return false;
  };

  while (true) {
    let readResult;
    try {
      readResult = await reader.read();
    } catch (readErr) {
      const msg = readErr instanceof Error ? readErr.message : String(readErr);
      const err = new Error(`Stream read failed (${path}): ${msg}`);
      onError?.(err);
      throw err;
    }
    const { value, done } = readResult;
    if (done) break;
    carry += decoder.decode(value, { stream: true });
    let sep;
    while ((sep = carry.indexOf('\n\n')) !== -1) {
      const block = carry.slice(0, sep).trim();
      carry = carry.slice(sep + 2);
      if (!block.startsWith('data:')) continue;
      let payload;
      try {
        payload = JSON.parse(block.slice(5).trim());
      } catch {
        continue;
      }
      if (handlePayload(payload)) {
        await awaitDisplay();
        return;
      }
    }
  }

  const tail = carry.trim();
  if (tail.startsWith('data:')) {
    try {
      const payload = JSON.parse(tail.slice(5).trim());
      if (handlePayload(payload)) {
        await awaitDisplay();
        return;
      }
    } catch {
      /* ignore */
    }
  }

  if (!sawDone) {
    emitDone?.({ done: true, usageEstimate: 0 });
  }
  await awaitDisplay();
  } finally {
    cleanupStreamGuards();
  }
};

export const getCloudBaseUrl = getBaseUrl;

/** Web PayPal subscription bootstrap (do not surface in native UI — App Store rules). */
export const createPayPalCloudSubscription = async () => postCloudAi('/api/paypal/create-subscription', {});
